import { fetchAuthSession, getCurrentUser } from '@aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { Amplify } from 'aws-amplify';
import {
  AuthorizationServer,
  Client,
  authorizationCodeGrantRequest,
  calculatePKCECodeChallenge,
  discoveryRequest,
  generateRandomCodeVerifier,
  isOAuth2Error,
  processAuthorizationCodeOpenIDResponse,
  processDiscoveryResponse,
  processRefreshTokenResponse,
  refreshTokenGrantRequest,
  validateAuthResponse,
} from 'oauth4webapi';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';
import Swal from 'sweetalert2';

const TOKEN_EXPIRATION_MARGIN = 10000;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TokenType = object & any;

export class TokenService {
  private authServicePromise?: Promise<AuthorizationServer>;

  private storePrefix = 'token-service::';
  private accessTokenKey = `${this.storePrefix}access_token`;
  private idTokenKey = `${this.storePrefix}id_token`;
  private refreshTokenKey = `${this.storePrefix}refresh_token`;

  private codeVerifierKey = `${this.storePrefix}code_verifier`;

  private refreshTimer?: ReturnType<typeof setTimeout>;

  private userLoggedInSubject = new BehaviorSubject(false);
  // Very important to emit only distinct values, as same value can be emitted with every check to isUserLoggedIn
  public readonly userLoggedIn = this.userLoggedInSubject.pipe(distinctUntilChanged());

  private amplifyConfigured = false;

  private idTokenInProgress?: Promise<void>;

  constructor(private config: TokenServiceConfig) {}

  async isUserLoggedIn() {
    await this.configureAmplify();

    const userSession = await fetchAuthSession();

    const exp = userSession.tokens?.idToken?.payload?.exp;
    const expired = this.isTokenExpired(exp);
    this.userLoggedInSubject.next(!expired);

    return !expired;
  }

  isTokenExpired(exp?: number) {
    return exp == null || exp * 1000 < Date.now() - TOKEN_EXPIRATION_MARGIN;
  }

  async getIdToken(): Promise<string> {
    return (await this.getIdTokenHolder()).toString();
  }

  async getIdTokenHolder() {
    await this.configureAmplify();

    let token = (await fetchAuthSession()).tokens?.idToken;
    if (token == null) {
      // TODO Use log in form to log in user, and unlock after token receive
      throw new Error('User not logged in');
    }

    if (this.isTokenExpired(token.payload.exp)) {
      token = (await fetchAuthSession({ forceRefresh: true })).tokens?.idToken;
    }

    if (token == null || this.isTokenExpired(token?.payload.exp)) {
      // Show error to the user
      throw new Error('Could not refresh token');
    }

    return token;
  }

  protected async configureAmplify() {
    let envConfig;
    if (!this.amplifyConfigured) {
      const response = await fetch('/env-config.json');
      envConfig = (await response.json()) as any;
    }
    // After async data load make sync configuration to prevent races
    // in configuration and double subscription to Hub
    if (!this.amplifyConfigured) {
      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolClientId: envConfig.oidc.client_id,
            userPoolId: envConfig.cognito.pool_id,
          },
        },
      });
      if (!this.amplifyConfigured) {
        Hub.listen('auth', ({ payload }) => {
          switch (payload.event) {
            case 'signedIn':
              this.isUserLoggedIn();
              break;
            case 'signedOut':
              this.isUserLoggedIn();
              break;
          }
        });
      }
    }
    this.amplifyConfigured = true;
  }

  /**
   * Performs full login, depending on options may redirect browser to
   * login URL (unsaved changes can be lost).
   */
  async getIdTokenOidc(): Promise<TokenHolder> {
    // 1. Get tokens fast from local storage or cache
    // 2. Validate tokens
    // 2.1 If not valid:
    // 2.1.1 If refresh token exists, try to refresh
    // 2.1.2 If refresh token does not exist, redirect to login page
    this.discoverAuthServer();

    try {
      const currentTokens = this.getTokenFast();
      if (this.validateTokens(currentTokens)) {
        return currentTokens;
      } else {
        const config = await this.loadConfig();
        if (window.location.href.startsWith(config.redirectUrl ?? 'non://existing')) {
          // This is race case when some service tries to load id token, while auth response is being processed.

          console.warn(
            'Delaying token response, due to possible race condition - trying to get token during auth callback',
          );
          return new Promise((resolve) => {
            setTimeout(() => resolve(null), 10);
          }).then(() => this.getIdTokenOidc());
        }
        try {
          return await this.refreshToken();
        } catch (_ignored) {}
      }

      const dialogResult = await Swal.fire({
        icon: 'error',
        title: $localize`Authentication required`,
        text: $localize`Your access token has expired or you are not logged in. You will be redirected to login page.`,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      if (dialogResult.isConfirmed) {
        return await this.doLogin();
      } else {
        return new TokenHolder();
      }
    } finally {
    }
  }

  async doLogin() {
    const config = await this.loadConfig();
    const authServerConfig = await this.discoverAuthServer();

    if (authServerConfig.authorization_endpoint == null) {
      throw new Error('No authorization endpoint in response');
    }

    if (config.client_id == null) {
      throw new Error('No client ID in config');
    }

    const code_verifier = generateRandomCodeVerifier();
    const code_challenge = await calculatePKCECodeChallenge(code_verifier);
    const code_challenge_method = 'S256';

    sessionStorage.setItem(this.codeVerifierKey, code_verifier);

    const authUrl = new URL(authServerConfig.authorization_endpoint);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('client_id', config.client_id);
    authUrl.searchParams.set('code_challenge', code_challenge);
    authUrl.searchParams.set('code_challenge_method', code_challenge_method);
    authUrl.searchParams.set('redirect_uri', config.redirectUrl);

    window.location.replace(authUrl.toString());

    // Should never reach this point, as we allow window re-loading
    await new Promise(() => {});
    return new TokenHolder();
  }
  /**
   * Callback function to be called when auth response with token, code or error
   * is received by application.
   */
  async processLoginResponse(): Promise<TokenHolder> {
    const config = await this.loadConfig();
    const authServerConfig = await this.discoverAuthServer();

    if (this.configHolder == null) {
      throw new Error('No config loaded');
    }

    const authResponse = validateAuthResponse(authServerConfig, config, new URL(window.location.href));
    if (isOAuth2Error(authResponse)) {
      console.error(JSON.stringify(authResponse));
      throw new Error(`OAuth2 error: ${authResponse.error} ${authResponse.error_description}`);
    }

    const codeVerifier = sessionStorage.getItem(this.codeVerifierKey);
    sessionStorage.removeItem(this.codeVerifierKey);
    if (codeVerifier == null) {
      throw new Error('No code verifier in session storage');
    }

    const codeResponseRaw = await authorizationCodeGrantRequest(
      authServerConfig,
      config,
      authResponse,
      config.redirectUrl,
      codeVerifier,
    );
    const codeResponse = await processAuthorizationCodeOpenIDResponse(authServerConfig, config, codeResponseRaw);

    if (isOAuth2Error(codeResponse)) {
      console.error('OAuth2 error: ' + JSON.stringify(codeResponse));
      throw new Error(`OAuth2 error: ${codeResponse.error} ${codeResponse.error_description}`);
    }

    this.holder = new TokenHolder(codeResponse.access_token, codeResponse.id_token, codeResponse.refresh_token);
    this.onTokensReceived(this.holder);
    return this.holder;
  }

  async refreshToken(): Promise<TokenHolder> {
    const config = await this.loadConfig();
    const refreshToken = this.getTokenFast().refreshTokenRaw;

    if (refreshToken == null) {
      throw new Error('No refresh token');
    }

    const authServerConfig = await this.discoverAuthServer();
    if (this.configHolder == null) {
      throw new Error('No config loaded');
    }

    const refreshResponsePlain = await refreshTokenGrantRequest(authServerConfig, config, refreshToken);
    const refreshResponse = await processRefreshTokenResponse(authServerConfig, config, refreshResponsePlain);

    if (isOAuth2Error(refreshResponse)) {
      throw new Error(`OAuth2 error: ${refreshResponse.error} ${refreshResponse.error_description}`);
    } else {
      this.holder = new TokenHolder(
        refreshResponse.access_token,
        refreshResponse.id_token,
        this.holder?.refreshTokenRaw,
      );
      this.onTokensReceived(this.holder);
      return this.holder;
    }
  }

  /** Removes tokens from local storage, invalidate tokens if possible. */
  clearStore() {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.idTokenKey);
    localStorage.removeItem(this.refreshTokenKey);

    sessionStorage.removeItem(this.codeVerifierKey);
  }

  private configHolder?: Promise<Client & TokenServiceEndpointsConfig>;
  protected async loadConfig(): Promise<Client & TokenServiceEndpointsConfig> {
    if (this.configHolder == null) {
      this.configHolder = this.config.configLoader().then((config) => ({
        token_endpoint_auth_method: 'none',
        ...config,
      }));
    }

    return this.configHolder;
  }

  private authorizationServerHolder?: Promise<AuthorizationServer>;
  protected async discoverAuthServer(): Promise<AuthorizationServer> {
    if (this.authorizationServerHolder == null) {
      this.authorizationServerHolder = (async () => {
        const config = await this.loadConfig();

        const authUrl = new URL(config.auth_server);
        const response = await discoveryRequest(authUrl);
        const authServerConfig = await processDiscoveryResponse(authUrl, response);
        return authServerConfig;
      })();
    }

    return this.authorizationServerHolder;
  }

  private holder?: TokenHolder;
  protected getTokenFast() {
    if (this.holder != null) {
      return this.holder;
    }

    // Try to load from local storage
    this.holder = new TokenHolder(
      localStorage.getItem(this.accessTokenKey),
      localStorage.getItem(this.idTokenKey),
      localStorage.getItem(this.refreshTokenKey),
    );
    this.onTokensReceived(this.holder);

    return this.holder;
  }

  protected validateTokens(holder: TokenHolder): boolean {
    const tokensValid = this.checkIfTokenRawIsValid(holder.accessToken) && this.checkIfTokenRawIsValid(holder.idToken);

    return tokensValid;
  }

  protected onTokensReceived(holder: TokenHolder): void {
    if (this.refreshTimer != null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }

    //TODO Add support for missing tokens, or single token response

    if (holder.accessTokenRaw != null) {
      localStorage.setItem(this.accessTokenKey, holder.accessTokenRaw.toString());
    }

    if (holder.idTokenRaw != null) {
      localStorage.setItem(this.idTokenKey, holder.idTokenRaw.toString());
    }

    if (holder.refreshTokenRaw != null) {
      localStorage.setItem(this.refreshTokenKey, holder.refreshTokenRaw.toString());
    }

    const tokenMinExp = Math.min(holder?.accessToken?.exp ?? 0, holder?.idToken?.exp ?? 0) * 1000;
    const refreshTime = tokenMinExp - Date.now() - TOKEN_EXPIRATION_MARGIN;

    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshToken();
      }, refreshTime);
    }
  }

  protected checkIfTokenRawIsValid(token?: TokenType): boolean {
    if (token == null) {
      return false;
    }
    const exp = token.exp;
    return exp != null && exp * 1000 > Date.now() + TOKEN_EXPIRATION_MARGIN;
  }
}

export class TokenHolder {
  public readonly idToken?: TokenType;
  public readonly accessToken?: TokenType;

  constructor(
    public readonly accessTokenRaw?: string | null,
    public readonly idTokenRaw?: string | null,
    public readonly refreshTokenRaw?: string | null,
  ) {
    //TODO: deep freeze, maybe
    const accessTokenContent = accessTokenRaw?.split('.')?.[1];
    if (accessTokenContent != null) {
      this.accessToken = Object.freeze(JSON.parse(atob(accessTokenContent)));
    }

    const idTokenContent = idTokenRaw?.split('.')?.[1];
    if (idTokenContent != null) {
      this.idToken = Object.freeze(JSON.parse(atob(idTokenContent)));
    }
  }
}

export interface GetTokenOptions {
  /** If reloading pages is allowed. Warning - unsaved changes can be lost */
  allowPageReload?: boolean;
}

export interface TokenServiceConfig {
  localStore?: {
    keyPrefix?: string;
  };
  configLoader: () => Promise<TokenServiceEndpointsConfig>;
}

export interface TokenServiceEndpointsConfig {
  auth_server: string;
  redirectUrl: string;
  client_id: string;
}
